import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const PaperSizeModal = ({ dimensions, onSubmit, onClose }) => {
  const { t } = useTranslation();
  // Par défaut, on suggère des dimensions 50% plus grandes que l'œuvre
  // pour le support papier (typique pour les gravures, aquarelles, etc.)
  const [paperSize, setPaperSize] = useState({
    height: dimensions?.height ? Math.round(dimensions.height * 1.5 * 10) / 10 : 0,
    width: dimensions?.width ? Math.round(dimensions.width * 1.5 * 10) / 10 : 0,
    unit: dimensions?.unit || "cm"
  });

  // Liste des formats standard de papier (facultatif)
  const standardSizes = [
    { name: "A4", height: 29.7, width: 21, unit: "cm" },
    { name: "A3", height: 42, width: 29.7, unit: "cm" },
    { name: "A2", height: 59.4, width: 42, unit: "cm" },
    { name: "A1", height: 84.1, width: 59.4, unit: "cm" },
    { name: "Raisin", height: 65, width: 50, unit: "cm" },
    { name: "Grand Aigle", height: 75, width: 105, unit: "cm" }
  ];

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setPaperSize({
      ...paperSize,
      [name]: type === "number" ? parseFloat(value) || 0 : value
    });
  };

  const applyStandardSize = (size) => {
    setPaperSize({
      height: size.height,
      width: size.width,
      unit: size.unit
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation de base
    if (paperSize.height <= 0 || paperSize.width <= 0) {
      toast.error(t("artworkModals.common.dimensionsPositive"));
      return;
    }
    
    onSubmit(paperSize);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
         onClick={onClose}>
      <div className="bg-white p-8 rounded-md max-w-lg w-full"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <h3 className="font-serif text-xl">{t("artworkModals.paperSize.title")}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-black">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t("artworkModals.paperSize.description")}
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-dark mb-1">
                  {t("artworkModals.common.height")}
                </label>
                <input
                  type="number"
                  name="height"
                  value={paperSize.height}
                  onChange={handleInputChange}
                  className="form-control-artsy w-full"
                  min="0"
                  step="0.1"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-dark mb-1">
                  {t("artworkModals.common.width")}
                </label>
                <input
                  type="number"
                  name="width"
                  value={paperSize.width}
                  onChange={handleInputChange}
                  className="form-control-artsy w-full"
                  min="0"
                  step="0.1"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-dark mb-1">
                {t("artworkModals.common.unit")}
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input 
                    type="radio" 
                    name="unit" 
                    value="cm"
                    checked={paperSize.unit === "cm"} 
                    onChange={handleInputChange} 
                    className="mr-2"
                  />
                  <span>{t("artworkModals.common.centimeters")}</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="radio" 
                    name="unit" 
                    value="inches"
                    checked={paperSize.unit === "inches"} 
                    onChange={handleInputChange} 
                    className="mr-2"
                  />
                  <span>{t("artworkModals.common.inches")}</span>
                </label>
              </div>
            </div>
            
            {/* Formats standard */}
            <div>
              <label className="block text-sm font-medium text-gray-dark mb-2">
                {t("artworkModals.paperSize.standardFormats")}
              </label>
              <div className="flex flex-wrap gap-2">
                {standardSizes.map((size) => (
                  <button
                    key={size.name}
                    type="button"
                    onClick={() => applyStandardSize(size)}
                    className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    {size.name} ({size.width} × {size.height} {size.unit})
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-dark mt-1">
                {t("artworkModals.paperSize.standardHelp")}
              </p>
            </div>
          </div>
          
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="btn-artsy btn-secondary-artsy mr-2"
            >
              {t("artworkModals.common.cancel")}
            </button>
            <button
              type="submit"
              className="btn-artsy btn-primary-artsy"
            >
              {t("artworkModals.common.add")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaperSizeModal;